import { useState } from "react";
import { GitBranch, Plus, X } from "lucide-react";

// Every feature `key` needs (direct + indirect), walking depends_on.
export function requiredClosure(key, byKey) {
  const out = new Set();
  const walk = (k) => (byKey[k]?.depends_on || []).forEach((d) => { if (!out.has(d)) { out.add(d); walk(d); } });
  walk(key);
  return out;
}

const Chip = ({ children, tone = "slate", onRemove, title }) => (
  <span title={title} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono whitespace-nowrap ${
    tone === "rose" ? "bg-rose-100 text-rose-800" : tone === "light" ? "bg-slate-50 text-slate-400 border border-slate-200" : tone === "blue" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-700"}`}>
    {children}
    {onRemove && <button type="button" onClick={onRemove} className="hover:text-rose-600" aria-label="Remove"><X className="w-3 h-3" /></button>}
  </span>
);

// "Requires" cell: editable direct dependencies + greyed indirect ones.
export function RequiresCell({ f, deps, byKey, allKeys, missing, onChange }) {
  const [adding, setAdding] = useState(false);
  const indirect = [...requiredClosure(f.key, { ...byKey, [f.key]: { ...f, depends_on: deps } })].filter((k) => !deps.includes(k));
  const options = allKeys.filter((k) => k !== f.key && !deps.includes(k));
  return (
    <div className="flex flex-wrap gap-1 items-center" data-testid={`feature-deps-${f.key}`}>
      {deps.length === 0 && <span className="text-slate-300 text-xs">none</span>}
      {deps.map((d) => (
        <Chip key={d} tone={missing.includes(d) ? "rose" : "slate"} onRemove={() => onChange(deps.filter((x) => x !== d))}>{d}</Chip>
      ))}
      {indirect.map((d) => <Chip key={d} tone="light" title="Needed indirectly">{d}</Chip>)}
      {adding ? (
        <select autoFocus className="border border-slate-300 rounded text-[11px] px-1 py-0.5 bg-white" data-testid={`feature-dep-add-select-${f.key}`}
          onChange={(e) => { if (e.target.value) onChange([...deps, e.target.value]); setAdding(false); }} onBlur={() => setAdding(false)} defaultValue="">
          <option value="">Add requirement…</option>
          {options.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      ) : (
        <button type="button" onClick={() => setAdding(true)} className="text-slate-400 hover:text-slate-700" title="Add requirement" data-testid={`feature-dep-add-${f.key}`}>
          <Plus className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// "Needed by" cell: every feature that depends on this one, all listed (wraps).
export function NeededByCell({ f }) {
  const list = f.dependents || [];
  return (
    <div className="flex flex-wrap gap-1 items-center" data-testid={`feature-dependents-${f.key}`}>
      {list.length === 0 ? <span className="text-slate-300 text-xs">—</span> : (
        <>
          <span className="inline-flex items-center gap-0.5 text-[11px] text-slate-500 mr-0.5"><GitBranch className="w-3 h-3" />{list.length}</span>
          {list.map((d) => <Chip key={d} tone="blue">{d}</Chip>)}
        </>
      )}
    </div>
  );
}
